/** Janela de vendas medida por nós — a API da Shopee não expõe período. */
/** Estado da IA na visão de admin: chave, crédito e consumo por cliente. */
export type PainelIa = {
  chave: {
    configurada: boolean;
    fonte: 'banco' | 'ambiente' | null;
    hint: string | null;
    desde: string | null;
  };
  modelo: string;
  modelosDisponiveis: string[];
  credito: {
    valorUsd: number | null;
    compradoEm: string | null;
    diasEsperados: number;
    diasRestantes: number | null;
    gastoDesdeUsd: number;
    saldoEstimadoUsd: number | null;
    chamadasSemPreco: number;
    alerta: 'ok' | 'prazo' | 'saldo' | 'sem_credito';
  };
  consumo: {
    totalUsd: number;
    chamadas: number;
    porCliente: Array<{
      accountId: string;
      nome: string;
      chamadas: number;
      custoUsd: number;
      tokensEntrada: number;
      tokensSaida: number;
    }>;
    ultimos7Dias: Array<{ dia: string; custoUsd: number; chamadas: number }>;
  };
};

export type JanelaVendas = 'dia' | 'semana' | 'mes';

export type OfferKind = 'PRODUCT' | 'SHOP' | 'CAMPAIGN';

export type Product = {
  id: string;
  kind: OfferKind;
  name: string;
  imageUrl: string | null;
  priceCents: number;
  commissionRate: number;
  commissionCents: number;
  sales: number;
  ratingStar: number | null;
  shopName: string | null;
  /** int64 da loja, como string. É a chave para buscar os itens dela. */
  shopeeShopId: string | null;
  priceDiscountRate: number | null;
  /** Comissão Xtra (parte do vendedor), quando existe. */
  sellerCommissionRate: number | null;
  salesPrevious: number | null;
  salesPreviousAt: string | null;
  /** Vendas no período escolhido, medidas por nós. Só vem com `window`. */
  salesWindow?: number;
  /** Dias de medição que esse número cobre — 3 dias não é uma semana. */
  salesWindowDays?: number;
  categoryPath: number[] | null;
  /** 1=Mall, 2=preferida, 4=preferida+. Json no banco, array aqui. */
  shopType: number[] | null;
  remainingBudget: number | null;
  campaignOfferType: number | null;
  offerEndsAt: string | null;
  /** Link SEM rastreio de afiliado — é o que o botão "Ver" abre. */
  originalUrl: string | null;
  offerLink: string | null;
  status: 'SUGGESTED' | 'SELECTED' | 'DISCARDED';
  discoveredAt: string;
  affiliateLinks: Array<{ subId: string; shortLink: string }>;
  _count?: { posts: number };
};

export type MediaAsset = {
  id: string;
  kind: 'IMAGE' | 'VIDEO';
  publicUrl: string;
  mimeType: string;
  sizeBytes: number;
  originalName: string | null;
  createdAt: string;
  warning?: string;
};

export type Platform = 'INSTAGRAM' | 'TIKTOK' | 'WHATSAPP';

export type Publication = {
  id: string;
  platform: Platform;
  status:
    | 'PENDING'
    | 'PUBLISHING'
    | 'PUBLISHED'
    | 'FAILED'
    | 'SKIPPED'
    | 'AWAITING_MANUAL';
  attempts: number;
  permalink: string | null;
  error: string | null;
  publishedAt: string | null;
  affiliateLink: string | null;
  nextAttemptAt: string | null;
};

export type Post = {
  id: string;
  caption: string;
  scheduledAt: string | null;
  status:
    | 'DRAFT'
    | 'SCHEDULED'
    | 'PUBLISHING'
    | 'PUBLISHED'
    | 'PARTIAL'
    | 'FAILED'
    | 'CANCELED';
  createdAt: string;
  product: { id: string; name: string; imageUrl: string | null; priceCents: number };
  mediaAsset: { id: string; kind: 'IMAGE' | 'VIDEO'; publicUrl: string } | null;
  publications: Publication[];
};

export type Integration = {
  provider: 'SHOPEE' | 'INSTAGRAM' | 'TIKTOK' | 'WHATSAPP';
  status: 'DISCONNECTED' | 'CONNECTED' | 'ERROR' | 'EXPIRED';
  fields: Record<string, string>;
  lastError?: string | null;
  expiresAt?: string | null;
};

/**
 * A estrutura do Reels, espelhando o servidor.
 *
 * Mudou junto com o formato: nome, gancho, corpo e remate — não mais bullets.
 * Se divergir do servidor, o desalinhamento é silencioso (o typecheck não
 * atravessa a rede), então este tipo acompanha adapters/ai/copy.ts.
 */
export type StructuredCopy = {
  hook: string;
  corpo: string;
  remate: string;
  hashtags: string[];
  coverTitle: string;
};

/**
 * Onde o trabalho está parado.
 *
 * O painel é um fluxo com etapas de verdade (oferta → produção → agenda →
 * publicado), então a contagem por etapa responde "o que fazer agora" melhor
 * que qualquer rótulo de menu.
 */
/**
 * Programação recorrente: um conteúdo que volta ao ar em horários fixos.
 *
 * Ela não publica — GERA posts na fila. Cada ocorrência aparece na Agenda como
 * post normal, editável e cancelável sozinha.
 */
export type Recurrence = {
  id: string;
  productId: string;
  mediaAssetId: string | null;
  caption: string;
  platforms: Platform[];
  /** "HH:MM" */
  times: string[];
  /** 0=domingo … 6=sábado; vazio = todos os dias */
  weekdays: number[];
  startsOn: string;
  /** null = por tempo indeterminado */
  endsOn: string | null;
  active: boolean;
  vencida: boolean;
  product: { id: string; name: string; imageUrl: string | null };
  _count: { posts: number };
};

/** Um dia da série. Dias sem venda vêm com zero, não ausentes. */
export type DiaDoRelatorio = {
  /** "AAAA-MM-DD" */
  dia: string;
  conversoes: number;
  canceladas: number;
  comissaoCents: number;
  vendidoCents: number;
};

/** Uma conta de cliente, na visão do administrador da plataforma. */
export type AdminAccount = {
  id: string;
  nome: string;
  criadaEm: string;
  usuarios: Array<{ email: string; name: string; role: string; lastLoginAt: string | null }>;
  integracoes: Array<{ provider: string; status: string }>;
  contagens: { products: number; posts: number; conversions: number };
  comissaoTotalCents: number;
  ultimaPublicacao: { publishedAt: string | null; platform: string } | null;
};

/** Um comentário tratado pela automação de respostas do Instagram. */
export type IgAutoReply = {
  id: string;
  commentId: string;
  mediaId: string;
  username: string | null;
  commentText: string | null;
  dmSent: boolean;
  commentSent: boolean;
  error: string | null;
  createdAt: string;
};

export type Pipeline = {
  ofertas: number;
  producao: number;
  agenda: number;
  publicados: number;
  problemas: number;
  aguardandoMao: number;
};

export type WhatsappMode =
  | 'cloud_api'
  | 'groups_api'
  | 'notify_manual'
  | 'web_automation';

export type WaSessionStatus =
  | 'IDLE'
  | 'CONNECTING'
  | 'QR'
  | 'CONNECTED'
  | 'LOGGED_OUT'
  | 'BUSY'
  | 'ERROR';

export type WaSession = {
  status: WaSessionStatus;
  /** data URL do QR code, presente só em status === 'QR' */
  qr?: string;
  /** segundos até o código na tela virar inútil; 0 = já expirou */
  qrExpiresInSec?: number;
  phone?: string;
  connectedAt?: string;
  lastError?: string;
  heldBy?: string;
  /** credencial em disco: reconectar não vai pedir QR de novo */
  paired: boolean;
};

export type WaGroup = {
  jid: string;
  name: string;
  participants: number;
  /** null = não deu para saber (identidade em LID não localizada) */
  isAdmin: boolean | null;
  announceOnly: boolean;
  /** false só quando há certeza de que o WhatsApp recusaria o envio */
  canPost: boolean;
};

export type Settings = {
  discoveryEnabled: boolean;
  discoveryIntervalHours: number;
  discoveryMinCommission: number;
  discoveryKeyword: string | null;
  discoveryLimit: number;
  igDailyCap: number;
  baseHashtags: string[];
  ctaComment: string;
  ctaStories: string;
  /** Nome do destaque do Instagram, usado no lugar de {destaque}. */
  igHighlightName: string | null;
  whatsappMode: string;
  igAutoReplyEnabled?: boolean;
  igAutoReplyTriggers?: string[];
  /** Textos da DM em rotação — a mesma mensagem repetida cai em filtro de spam. */
  igAutoReplyDms: string[];
  igAutoReplyComment?: string;
  defaultTiktokPrivacy: string;
};

export type ChannelPerf = {
  channel: string | null;
  conversions: number;
  items: number;
  purchaseValueCents: number;
  totalCommissionCents: number;
  netCommissionCents: number;
};

export type ProductPerf = {
  productId: string | null;
  shopeeItemId: string | null;
  name: string | null;
  imageUrl: string | null;
  qty: number;
  purchaseValueCents: number;
};

export type SyncRun = {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  fetched: number;
  upserted: number;
  pages: number;
  truncated: boolean;
  ok: boolean;
  error: string | null;
};

export type Overview = {
  days: number;
  since: string;
  totals: {
    conversions: number;
    /** Conversões canceladas: ficam fora dos valores, contadas à parte. */
    cancelled: number;
    items: number;
    purchaseValueCents: number;
    totalCommissionCents: number;
    netCommissionCents: number;
  };
  byChannel: ChannelPerf[];
  byProduct: ProductPerf[];
  lastSync: SyncRun | null;
};

export type Conversion = {
  id: string;
  conversionId: string;
  purchaseTime: string | null;
  channel: string | null;
  utmContent: string | null;
  device: string | null;
  buyerType: string | null;
  orderStatus: string | null;
  itemCount: number;
  purchaseValueCents: number;
  totalCommissionCents: number;
  netCommissionCents: number;
  items: Array<{
    itemName: string | null;
    imageUrl: string | null;
    qty: number;
    actualAmountCents: number;
    status: string | null;
    productId: string | null;
  }>;
};

export type CatalogFeed = {
  id: string;
  datafeedId: string;
  name: string;
  description: string | null;
  totalCount: number;
  feedMode: string;
  feedDate: string | null;
  importedAt: string | null;
  importedRows: number;
  importedNew: number;
  importError: string | null;
};

export type ValidatedRow = {
  conversionId: string | null;
  purchaseTime: string | null;
  channel: string | null;
  buyerType: string | null;
  device: string | null;
  totalCommissionCents: number;
  netCommissionCents: number;
  purchaseValueCents: number;
  itemNames: string[];
};

export type Session = {
  token: string;
  user: {
    id: string;
    name: string;
    email: string;
    role?: string;
    /** true quando é o admin da plataforma dentro da conta de um cliente */
    impersonated?: boolean;
    account: { id: string; name: string; slug: string };
  };
};

const TOKEN_KEY = 'shopee-automation-token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly issues?: Array<{ path: string; message: string }>,
  ) {
    super(message);
  }
}

async function call<T>(
  method: string,
  path: string,
  body?: unknown,
  form?: FormData,
): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.authorization = `Bearer ${token}`;
  if (body !== undefined) headers['content-type'] = 'application/json';

  const res = await fetch(path, {
    method,
    headers,
    body: form ?? (body === undefined ? undefined : JSON.stringify(body)),
  });

  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const err = parsed as { message?: string; issues?: Array<{ path: string; message: string }> };
    // 401 significa sessão morta: limpamos para o app voltar ao login em vez de
    // ficar num loop de requisições rejeitadas.
    if (res.status === 401) setToken(null);
    throw new ApiError(res.status, err?.message ?? `Erro HTTP ${res.status}`, err?.issues);
  }
  return parsed as T;
}

export const api = {
  health: () =>
    call<{ ok: boolean; mockMode: boolean; storage: string; aiConfigured: boolean }>(
      'GET',
      '/api/health',
    ),

  register: (input: {
    email: string;
    password: string;
    name: string;
    accountName: string;
  }) => call<Session>('POST', '/api/auth/register', input),

  login: (input: { email: string; password: string }) =>
    call<Session>('POST', '/api/auth/login', input),

  me: () => call<Session['user']>('GET', '/api/auth/me'),

  products: (
    params: {
      status?: string;
      kind?: OfferKind;
      search?: string;
      /** Só os itens de uma loja (int64 como string). */
      shopId?: string;
      take?: number;
      categoryId?: number;
      minCommission?: number;
      sort?: 'sales' | 'commission' | 'price_asc' | 'price_desc' | 'recent';
      /** Ranqueia pela venda medida no período em vez do contador absoluto. */
      window?: JanelaVendas;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.kind) qs.set('kind', params.kind);
    if (params.search) qs.set('search', params.search);
    if (params.shopId) qs.set('shopId', params.shopId);
    if (params.categoryId) qs.set('categoryId', String(params.categoryId));
    if (params.minCommission) qs.set('minCommission', String(params.minCommission));
    if (params.sort) qs.set('sort', params.sort);
    if (params.window) qs.set('window', params.window);
    qs.set('take', String(params.take ?? 100));
    return call<{
      items: Product[];
      total: number;
      /** Só com `window`: quanto histórico existe, para a tela não mentir. */
      cobertura?: { dias: number; produtos: number };
    }>('GET', `/api/products?${qs}`);
  },

  discover: (
    sources: Array<'products' | 'shops' | 'campaigns'> = ['products'],
    filters: {
      keyword?: string | null;
      /** Só produtos: itens de uma loja específica (int64 como string). */
      shopId?: string | null;
      categoryId?: number | null;
      sortType?: number | null;
      minCommission?: number | null;
      isKeySeller?: boolean | null;
      isAMSOffer?: boolean | null;
      shopType?: number[] | null;
    } = {},
  ) =>
    call<{
      found: number;
      created: number;
      ok: boolean;
      bySource?: Record<string, { found: number; created: number }>;
      /** Categorias varridas quando a Shopee ignoraria a ordenação. */
      sweptCategories?: number;
    }>('POST', '/api/products/discover', { sources, ...filters }),

  feeds: () => call<CatalogFeed[]>('GET', '/api/products/feeds'),
  refreshFeeds: (feedMode: 'FULL' | 'DELTA' = 'FULL') =>
    call<{ feeds: number }>('POST', '/api/products/feeds/refresh', { feedMode }),
  importFeed: (datafeedId: string) =>
    call<{
      rows: number;
      found: number;
      created: number;
      truncated: boolean;
      unmappedColumns: string[];
    }>('POST', `/api/products/feeds/${encodeURIComponent(datafeedId)}/import`, {}),

  validated: (limit = 200) =>
    call<{ rows: ValidatedRow[]; totalCommissionCents: number; truncated: boolean }>(
      'GET',
      `/api/reports/validated?limit=${limit}`,
    ),

  setProductStatus: (id: string, status: Product['status']) =>
    call<{ id: string; status: string }>('PATCH', `/api/products/${id}/status`, { status }),

  affiliateLink: (id: string, subId: 'instagram' | 'tiktok' | 'whatsapp' | 'manual') =>
    call<{ shortLink: string }>('POST', `/api/products/${id}/affiliate-link`, { subId }),

  uploadMedia: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return call<MediaAsset>('POST', '/api/content/media', undefined, form);
  },

  mediaList: () => call<MediaAsset[]>('GET', '/api/content/media'),

  generateCopy: (input: {
    productId: string;
    platform: 'instagram' | 'tiktok' | 'whatsapp';
    tone: 'urgencia' | 'promocional' | 'informal' | 'informativo';
    extraInstructions?: string;
    /** Cupom e parcela não vêm da API da Shopee — só o dono tem. */
    couponLabel?: string | null;
    couponCode?: string | null;
    installmentLabel?: string | null;
  }) =>
    call<{ variants: string[]; structured: StructuredCopy | null; model: string }>(
      'POST',
      '/api/content/copy',
      input,
    ),

  posts: (params: { status?: string; productId?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.productId) qs.set('productId', params.productId);
    qs.set('take', '200');
    return call<Post[]>('GET', `/api/posts?${qs}`);
  },

  createPost: (input: {
    productId: string;
    mediaAssetId?: string | null;
    caption?: string;
    scheduledAt?: string | null;
    platforms: Platform[];
    options?: Partial<Record<Platform, Record<string, unknown>>>;
  }) => call<Post>('POST', '/api/posts', input),

  scheduleBatch: (input: {
    items: Array<{ productId: string; mediaAssetId?: string | null; caption?: string }>;
    platforms: Platform[];
    startAt: string;
    intervalMinutes: number;
    /** Gera o texto de cada item pela IA — o modo "pega 10 e agenda". */
    generateCaption?: boolean;
  }) =>
    call<{ created: number; firstAt: string; lastAt: string; igCapApplied: number | null }>(
      'POST',
      '/api/posts/batch',
      input,
    ),

  updatePost: (id: string, patch: { caption?: string; scheduledAt?: string }) =>
    call<Post>('PATCH', `/api/posts/${id}`, patch),
  cancelPost: (id: string) => call<{ id: string }>('POST', `/api/posts/${id}/cancel`),
  deletePost: (id: string) => call<null>('DELETE', `/api/posts/${id}`),

  integrations: () =>
    call<{ mockMode: boolean; liveAdapters: string[]; integrations: Integration[] }>(
      'GET',
      '/api/integrations',
    ),

  saveShopee: (input: { appId: string; secret: string }) =>
    call<unknown>('PUT', '/api/integrations/shopee', input),
  saveInstagram: (input: { igUserId: string; accessToken: string }) =>
    call<unknown>('PUT', '/api/integrations/instagram', input),
  saveTiktok: (input: { accessToken: string; refreshToken: string; openId?: string }) =>
    call<unknown>('PUT', '/api/integrations/tiktok', input),
  saveWhatsapp: (input: {
    mode: WhatsappMode;
    phoneNumberId?: string;
    accessToken?: string;
    recipients?: string[];
    groupId?: string;
    groupJid?: string;
    groupName?: string;
  }) => call<unknown>('PUT', '/api/integrations/whatsapp', input),

  // ------------------------------------------- sessão pareada (web_automation)
  waSessionStart: () =>
    call<WaSession>('POST', '/api/integrations/whatsapp/session'),
  waSessionStatus: () => call<WaSession>('GET', '/api/integrations/whatsapp/session'),
  waSessionGroups: () =>
    call<{ groups: WaGroup[]; selectedJid: string | null }>(
      'GET',
      '/api/integrations/whatsapp/session/groups',
    ),
  waSessionPause: () =>
    call<WaSession>('POST', '/api/integrations/whatsapp/session/pause'),
  /** Desconecta e apaga o pareamento — é o caminho de trocar de número. */
  waSessionLogout: () => call<WaSession>('DELETE', '/api/integrations/whatsapp/session'),

  // ------------------------------------------------------- admin da plataforma
  /**
   * Login do admin — porta separada da dos clientes.
   *
   * O servidor recusa cliente aqui e recusa admin no /login. A separação é
   * real, não só de tela.
   */
  adminLogin: (input: { email: string; password: string }) =>
    call<Session>('POST', '/api/auth/admin/login', input),

  adminAccounts: () => call<AdminAccount[]>('GET', '/api/admin/accounts'),

  adminAi: () => call<PainelIa>('GET', '/api/admin/ai'),
  adminSetAiKey: (key: string) => call<PainelIa>('PUT', '/api/admin/ai/key', { key }),
  adminClearAiKey: () => call<PainelIa>('DELETE', '/api/admin/ai/key'),
  adminSetAiModel: (model: string) =>
    call<PainelIa>('PUT', '/api/admin/ai/model', { model }),
  adminSetAiCredit: (valorUsd: number, dias: number) =>
    call<PainelIa>('PUT', '/api/admin/ai/credit', { valorUsd, dias }),
  adminEnter: (accountId: string) =>
    call<{ token: string; conta: { id: string; nome: string } }>(
      'POST',
      `/api/admin/accounts/${accountId}/enter`,
    ),

  overview: (days = 30) => call<Overview>('GET', `/api/reports/overview?days=${days}`),

  /** Série diária para o gráfico: conversões e comissão por dia. */
  daily: (days = 30) =>
    call<{ days: number; series: DiaDoRelatorio[] }>(
      'GET',
      `/api/reports/daily?days=${days}`,
    ),

  /** Contagem de cada etapa do fluxo — alimenta o trilho da barra do topo. */
  pipeline: () => call<Pipeline>('GET', '/api/reports/pipeline'),

  /** Histórico da automação de respostas do Instagram. */
  igReplies: () => call<IgAutoReply[]>('GET', '/api/inbox/replies'),

  // -------------------------------------------------- programação recorrente
  recurrences: () => call<Recurrence[]>('GET', '/api/posts/recurrences'),
  createRecurrence: (input: {
    productId: string;
    mediaAssetId?: string | null;
    caption?: string;
    platforms: Platform[];
    options?: Record<string, unknown>;
    times: string[];
    weekdays?: number[];
    startsOn?: string;
    endsOn?: string | null;
  }) =>
    call<Recurrence & { postsCriados: number }>('POST', '/api/posts/recurrences', input),
  toggleRecurrence: (id: string, active: boolean) =>
    call<Recurrence>('PATCH', `/api/posts/recurrences/${id}`, { active }),
  deleteRecurrence: (id: string) =>
    call<{ removida: boolean; postsCancelados: number }>(
      'DELETE',
      `/api/posts/recurrences/${id}`,
    ),
  syncConversions: (lookbackDays?: number) =>
    call<{ fetched: number; upserted: number; truncated: boolean }>(
      'POST',
      '/api/reports/sync',
      lookbackDays ? { lookbackDays } : {},
    ),
  conversions: (params: { days?: number; channel?: string } = {}) => {
    const qs = new URLSearchParams();
    qs.set('days', String(params.days ?? 30));
    if (params.channel) qs.set('channel', params.channel);
    return call<Conversion[]>('GET', `/api/reports/conversions?${qs}`);
  },

  settings: () => call<Settings>('GET', '/api/integrations/settings'),
  saveSettings: (patch: Partial<Settings>) =>
    call<Settings>('PATCH', '/api/integrations/settings', patch),
};

export function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Separa a moeda dos dígitos.
 *
 * Serve ao cartão de métrica: com "R$" menor e discreto, os dígitos de vários
 * cartões alinham na mesma altura e a fileira lê como coluna de extrato. Com a
 * moeda no mesmo tamanho, cada número começa numa posição diferente.
 */
export function formatBRLParts(cents: number): { unit: string; digits: string } {
  return {
    unit: 'R$',
    digits: (cents / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
  };
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Valor para <input type="datetime-local">, que exige hora local sem timezone. */
export function toLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}
